
import Vue from "vue";
import { Component, Watch, Prop } from "vue-property-decorator";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import TooltipLabel from "@/components/general/TooltipLabel.vue";
import Formatter from "@/mixins/formatterMixin";
import { PercentIndicator } from "@/mixins/percentIndicatorMixin";
import SensitiveNumbersMixin from "@/mixins/sensitiveNumbersMixin";
import { censorDataIfNeeded } from "@/utils/sensitiveNumbersUtils";
import CensorableChart from "@/components/chart/CensorableChart.vue";
import {
  generatePositiveAndNegativeChartBackground,
  getTimeScales,
} from "@/utils/chartUtils";
import { yAxisInteractionLine } from "@/plugins/chartjs";

/**
 * 🎭 Interface which represents a coordinates of a single point in the chart
 */
interface Coordinates {
  x: Date;
  y: number;
}

@Component({
  components: { TooltipLabel },
  mixins: [Formatter, PercentIndicator, SensitiveNumbersMixin],
})
export default class ProfitabilityChart extends CensorableChart {
  // 🎨 Possible views that can be shown in this component
  private ComponentsToShow = {
    Loading: 0,
    Placeholder: 1,
    Chart: 2,
  };

  // 📦 The currently displayed component
  private currentlyShownComponent: number = this.ComponentsToShow.Loading;

  // 📦 The starting balance for the selected time period
  @Prop()
  public initialBalanceForPeriod!: number | null;

  // 📦 The profit of the bots over time
  private profitabilityDataSet: number[] = [];

  // 📦 Dates for each profitability check-up
  private dateLabels: Date[] = [];

  // 📦 The selected portfolio chart filter in days
  @Prop({ default: 1 })
  public selectedFilterInDays!: number;

  // 📦📈 Chart which displays the profile profitability over time
  private profitabilityChart: any | null = null;

  // 📦 The total profit generated during the selected period
  private profitabilityForPeriod: number | null = null;

  // 📦 The profitability change for the selected period as a fraction
  private get profitabilityChangeForPeriod(): number {
    if (this.profitabilityForPeriod && this.initialBalanceForPeriod) {
      return (this.profitabilityForPeriod / this.initialBalanceForPeriod) * 100;
    }
    return 0;
  }

  /**
   * 👂 Listener used in order to determine the selected chart filter mode
   */
  @Watch("selectedFilterInDays")
  private onDaysFilterChanged(val: number, oldVal?: number) {
    this.fetchProfileProfitability();
  }

  protected mounted() {
    this.fetchProfileProfitability();
  }

  /**
   * 🚀 Fetches the portfolio value per exchange from the API
   */
  private async fetchProfileProfitability() {
    Vue.prototype.$api
      .get("/api/v1/profileprofit", this.getRequestParams())
      .then((response: any) => {
        // Reset data sets
        this.dateLabels = [];
        this.profitabilityDataSet = [];

        this.dateLabels = Object.keys(response.data).map(
          (value: String, index: Number, array: String[]) => {
            return new Date(
              Number(value) * 1000 - new Date().getTimezoneOffset() * 60
            );
          }
        );
        this.profitabilityDataSet = Object.values(response.data);

        this.calculateTotalProfitForPeriod(this.profitabilityDataSet);

        // If the placeholder should not be shown show chart
        if (this.profitabilityDataSet.length > 0) {
          this.currentlyShownComponent = this.ComponentsToShow.Chart;
          this.createChart();
        } else {
          // Show placeholder
          this.currentlyShownComponent = this.ComponentsToShow.Placeholder;
        }
      });
  }

  /**
   * 🏗 Calculate the total profit generated in the selected period
   */
  private calculateTotalProfitForPeriod(dataset: number[]) {
    if (dataset.length > 0) {
      this.profitabilityForPeriod =
        dataset.length > 1
          ? dataset[dataset.length - 1] - dataset[0]
          : dataset[0];
    }
  }

  /**
   * 🔨 Returns the request parameters needed when requesting the profitability
   */
  private getRequestParams(): any {
    if (this.selectedFilterInDays != -1) {
      return { days: this.selectedFilterInDays };
    }
  }

  /**
   * 🎨 Draws a dynamic chart on the canvas which is responsible for displaying the profitability
   */
  protected createChart() {
    if (this.profitabilityChart == null) {
      let container: any = document.getElementById(
        "container-profitability-chart"
      );
      this.profitabilityChart = new Chart(container, {
        type: "line",
        data: {
          labels: this.dateLabels,
          datasets: generatePositiveAndNegativeChartBackground(
            this.$vuetify,
            "Profitability",
            this.$refs.containerProfitabilityChart as any,
            this.profitabilityDataSet
          ),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: getTimeScales(this.$vuetify, this.selectedFilterInDays!! == 1),
            y: this.getYAxes(),
          },
          interaction: {
            intersect: false,
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              mode: "index",
              titleFont: { family: "Aeroport" },
              bodyFont: { family: "Aeroport" },
              callbacks: {
                label: (context: any) => {
                  return (
                    context.dataset.label +
                    ": $" +
                    censorDataIfNeeded(Number(context.parsed.y).toFixed(2))
                  );
                },
              },
            },
          },
        },
        plugins: [yAxisInteractionLine(this.$vuetify)],
      });
    } else {
      this.profitabilityChart.data.datasets = generatePositiveAndNegativeChartBackground(
        this.$vuetify,
        "Profitability",
        this.$refs.containerProfitabilityChart as any,
        this.profitabilityDataSet
      );
      this.profitabilityChart.data.labels = this.dateLabels;
      this.profitabilityChart.options.scales!!.x = getTimeScales(
        this.$vuetify,
        this.selectedFilterInDays!! == 1
      );
      this.profitabilityChart.options.scales!!.y = this.getYAxes();
      this.profitabilityChart.update();
    }
  }

  /**
   * 🔨 Calculates a dynamic y axis based on the provided dataset
   */
  private getYAxes(): any {
    // Minimal value of the dataset
    let minValue: number = Math.min(...this.profitabilityDataSet);

    // Maximum value of the dataset
    let maxValue: number = Math.max(...this.profitabilityDataSet);

    // Boundary which is equal to 5% of the difference between the maximum and minimum value of the dataset
    let chartBoundary: number =
      maxValue - minValue == 0
        ? Math.ceil(maxValue * 0.05)
        : Math.ceil(Math.abs(maxValue - minValue) * 0.05);

    // Minimum y value that should be displayed on the chart
    let minDisplayValue: number = this.getMinDisplayValue(
      minValue,
      maxValue,
      chartBoundary
    );

    // Maximum y value that should be displayed on the chart
    let maxDisplayValue: number = this.getMaxDisplayValue(
      minValue,
      maxValue,
      chartBoundary
    );

    // Step size which is equal to 15% of the difference between the maximum and minimum display value of the dataset
    let stepSize: number =
      maxDisplayValue - minDisplayValue == 0
        ? 1
        : (maxDisplayValue - minDisplayValue) * 0.15;

    return {
      ticks: {
        min: minDisplayValue,
        max: maxDisplayValue,
        stepSize: stepSize,
        maxTicksLimit: 8,
        callback: this.getYAxisDataFormater,
        color: this.$vuetify.theme.currentTheme.chartLabel,
        font: { family: "Aeroport" },
      },
      grid: {
        color: this.$vuetify.theme.currentTheme.chartGridColor,
        borderColor: this.$vuetify.theme.currentTheme.chartGridColor,
      },
      stacked: true,
    };
  }

  /**
   * 🔨 Callback function that is used to format the y-axis values
   */
  private getYAxisDataFormater(
    value: number,
    index: number,
    values: number[]
  ): string {
    return (
      "$" +
      censorDataIfNeeded(
        Math.max(...values) - Math.min(...values) >= 50
          ? value.toFixed(0)
          : value.toFixed(2)
      )
    );
  }

  /**
   * 🏗 Calculates and returns the minimum display value
   */
  private getMinDisplayValue(
    minValue: number,
    maxValue: number,
    chartBoundary: number
  ) {
    if (maxValue - minValue < 1) {
      return Math.floor(minValue + -chartBoundary);
    }
    return Math.floor(minValue);
  }

  /**
   * 🏗 Calculates and returns the maximum display value
   */
  private getMaxDisplayValue(
    minValue: number,
    maxValue: number,
    chartBoundary: number
  ) {
    if (maxValue - minValue < 1) {
      return Math.ceil(maxValue + chartBoundary);
    }
    return Math.ceil(maxValue);
  }
}
